import { useState, useEffect, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { updateAgent } from '../data';
import './AgentPages.css';
import { CheckCircle, Ban, Palette } from 'lucide-react';

const doSuggestions = ["Greet warmly", "Confirm understanding", "Offer alternatives", "Stay professional"];
const dontSuggestions = ["Share data", "Make false promises", "Use jargon", "Argue with customers"];
const AUTOSAVE_DELAY_MS = 800;

export default function AgentTeaching() {
  const { agent, setAgent, agentId } = useOutletContext();
  const [status, setStatus] = useState('idle'); // 'idle' | 'pending' | 'saving' | 'saved' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  const [form, setForm] = useState({
    doGuidelines: '',
    avoidGuidelines: '',
    toneFormality: 50,
    toneEnthusiasm: 50,
  });

  const hydratedRef = useRef(false);
  const timerRef = useRef(null);
  const latestRef = useRef(form);
  const savedTimerRef = useRef(null);

  useEffect(() => {
    if (agent) {
      const next = {
        doGuidelines: agent.doGuidelines || '',
        avoidGuidelines: agent.avoidGuidelines || '',
        toneFormality: agent.toneFormality ?? 50,
        toneEnthusiasm: agent.toneEnthusiasm ?? 50,
      };
      setForm(next);
      latestRef.current = next;
      hydratedRef.current = true;
    }
  }, [agent]);

  const flushSave = useCallback(async () => {
    const payload = latestRef.current;
    setStatus('saving');
    setErrorMsg('');
    try {
      await updateAgent(agentId, payload);
      setAgent((prev) => ({ ...(prev || {}), ...payload }));
      setStatus('saved');
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => {
        setStatus((s) => (s === 'saved' ? 'idle' : s));
      }, 1500);
    } catch (err) {
      console.error('Auto-save failed:', err);
      setErrorMsg(err?.message || 'Save failed');
      setStatus('error');
    }
  }, [agentId, setAgent]);

  const scheduleSave = useCallback(() => {
    if (!hydratedRef.current) return;
    setStatus('pending');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      flushSave();
    }, AUTOSAVE_DELAY_MS);
  }, [flushSave]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);

  // Flush pending edits on tab close/navigation.
  useEffect(() => {
    const onBeforeUnload = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        flushSave();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [flushSave]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      latestRef.current = next;
      return next;
    });
    scheduleSave();
  };

  const addTo = (field, text) => {
    const current = form[field];
    handleChange(field, current ? `${current}\n• ${text}` : `• ${text}`);
  };

  const getLabel = (value, type) => {
    if (type === 'formality') {
      if (value < 25) return "Very Casual";
      if (value < 50) return "Casual";
      if (value < 75) return "Professional";
      return "Formal";
    }
    if (value < 25) return "Reserved";
    if (value < 50) return "Balanced";
    if (value < 75) return "Friendly";
    return "Enthusiastic";
  };

  const statusLabel = (() => {
    switch (status) {
      case 'pending': return 'Editing…';
      case 'saving': return 'Saving…';
      case 'saved': return 'Saved';
      case 'error': return errorMsg || 'Save failed';
      default: return 'All changes saved';
    }
  })();

  return (
    <div className="config-page">
      <div className="config-header">
        <h1>Behavior & Guardrails</h1>
        <p>Define what your agent should and shouldn't do</p>
        <span className={`autosave-badge autosave-${status}`}>{statusLabel}</span>
      </div>

      <div className="config-section">
        <div className="section-header">
          <div className="section-icon"><CheckCircle size={18} /></div>
          <div className="section-text">
            <h2>Do's</h2>
            <p>Behaviors your agent should always follow</p>
          </div>
        </div>

        <div className="form-group">
          <textarea
            className="input textarea"
            placeholder="• Always greet warmly&#10;• Confirm understanding&#10;• Be helpful"
            value={form.doGuidelines}
            onChange={(e) => handleChange('doGuidelines', e.target.value)}
          />
          <div className="suggestions">
            <div className="suggestions-title">Click to add</div>
            <div className="suggestions-list">
              {doSuggestions.map((s, i) => (
                <button key={i} className="suggestion" onClick={() => addTo('doGuidelines', s)}>
                  + {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="config-section">
        <div className="section-header">
          <div className="section-icon"><Ban size={18} /></div>
          <div className="section-text">
            <h2>Don'ts</h2>
            <p>Things your agent should never do</p>
          </div>
        </div>

        <div className="form-group">
          <textarea
            className="input textarea"
            placeholder="• Never share customer data&#10;• Don't make promises&#10;• Avoid technical jargon"
            value={form.avoidGuidelines}
            onChange={(e) => handleChange('avoidGuidelines', e.target.value)}
          />
          <div className="suggestions">
            <div className="suggestions-title">Click to add</div>
            <div className="suggestions-list">
              {dontSuggestions.map((s, i) => (
                <button key={i} className="suggestion" onClick={() => addTo('avoidGuidelines', s)}>
                  + {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="config-section">
        <div className="section-header">
          <div className="section-icon"><Palette size={18} /></div>
          <div className="section-text">
            <h2>Tone & Style</h2>
            <p>How your agent communicates</p>
          </div>
        </div>

        <div className="slider-container">
          <div className="slider-header">
            <span className="slider-label">Formality</span>
            <span className="slider-value">{getLabel(form.toneFormality, 'formality')}</span>
          </div>
          <div className="slider-track">
            <span className="slider-end">Casual</span>
            <input
              type="range"
              className="slider"
              min="0"
              max="100"
              value={form.toneFormality}
              onChange={(e) => handleChange('toneFormality', parseInt(e.target.value))}
            />
            <span className="slider-end">Formal</span>
          </div>
        </div>

        <div className="slider-container">
          <div className="slider-header">
            <span className="slider-label">Energy</span>
            <span className="slider-value">{getLabel(form.toneEnthusiasm, 'enthusiasm')}</span>
          </div>
          <div className="slider-track">
            <span className="slider-end">Reserved</span>
            <input
              type="range"
              className="slider"
              min="0"
              max="100"
              value={form.toneEnthusiasm}
              onChange={(e) => handleChange('toneEnthusiasm', parseInt(e.target.value))}
            />
            <span className="slider-end">Energetic</span>
          </div>
        </div>
      </div>
    </div>
  );
}
